import { ChevronRight, Plane } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import type { ReadinessSummary } from "../../services/api/travelHealth";
import type { Travel } from "../../services/api/travels";
import { ReadinessRing } from "../ui/ReadinessRing";
import { useI18nStore } from "../../stores/i18nStore";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { parseDateValue } from "../../utils/dateLocalization";
import { daysUntil } from "./homeLogic";
import { fill } from "./fill";

interface Props {
  trip: Travel | null;
  /** Further upcoming trips after this one. */
  moreCount: number;
  /** The trip's readiness; null until its checklist has been built. */
  readiness: ReadinessSummary | null;
  now: Date;
}

function tripName(trip: Travel): string {
  return trip.destination || trip.country || trip.tripTitle || "—";
}

function shortDate(iso: string | undefined, locale: string): string {
  const d = parseDateValue(iso);
  if (!d) return "—";
  return d.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

const Perforation: React.FC = () => (
  <div
    className="relative w-0 border-l-2 border-dashed border-border"
    aria-hidden="true"
  >
    <div className="absolute -left-[11px] -top-2.5 h-5 w-5 rounded-full bg-ground" />
    <div className="absolute -bottom-2.5 -left-[11px] h-5 w-5 rounded-full bg-ground" />
  </div>
);

/**
 * The next trip as a boarding pass (UX_CONCEPT.md §5.1). With no trip
 * planned, the same pass invites the traveller to plan one.
 *
 * The stub shows the readiness ring, computed from the trip's own checklist
 * (backend/src/services/tripReadiness.ts). Before the checklist is built it is
 * a dashed ring, and the strip invites the traveller to look.
 */
export const NextTripPass: React.FC<Props> = ({
  trip,
  moreCount,
  readiness,
  now,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();

  if (!trip) {
    return (
      <section
        aria-label={t.homeNoTripTitle}
        className="overflow-hidden rounded-[22px] bg-white shadow-sm"
      >
        <div className="p-5">
          <div className="text-[11px] font-bold tracking-[0.14em] text-muted-foreground">
            {t.homeNextTrip.toUpperCase()}
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold text-ink">
            {t.homeNoTripTitle}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {t.homeNoTripBody}
          </p>
        </div>
        <Link
          to="/traveler/travels/new"
          className="flex min-h-[52px] items-center justify-between bg-travel-500 px-5 text-[15px] font-bold text-ink"
        >
          {t.homePlanTrip}
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </Link>
      </section>
    );
  }

  const days = daysUntil(trip.departureDate, now);
  const percent = readiness?.percent ?? null;
  const open = readiness ? readiness.total - readiness.done : 0;
  const strip =
    percent === null
      ? t.homeCheckTrip
      : percent === 100
        ? t.readinessAllDone
        : `${open} ${t.readinessStepsLeft}`;
  const nights =
    trip.departureDate && trip.returnDate
      ? daysUntil(trip.returnDate, new Date(trip.departureDate))
      : null;
  const when =
    days === null
      ? ""
      : days <= 0
        ? t.homeToday
        : days === 1
          ? t.homeTomorrow
          : fill(t.homeInDays, { n: days });

  return (
    <section
      aria-label={`${t.homeNextTrip}: ${tripName(trip)}`}
      className="overflow-hidden rounded-[22px] bg-white shadow-[0_1px_2px_rgba(17,22,58,0.06),0_12px_32px_-18px_rgba(17,22,58,0.25)]"
    >
      <div className="flex">
        <div className="min-w-0 flex-1 p-5">
          <div className="text-[11px] font-bold tracking-[0.14em] text-muted-foreground">
            {t.homeNextTrip.toUpperCase()}
            {when && ` · ${when.toUpperCase()}`}
          </div>
          <h2 className="mt-1.5 truncate font-display text-[28px] font-extrabold leading-tight text-ink">
            {tripName(trip)}
          </h2>
          {trip.startLocation && (
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <span className="truncate">{trip.startLocation}</span>
              <Plane
                className="h-4 w-4 flex-shrink-0 rotate-45 text-primary-600"
                aria-hidden="true"
              />
              <span className="truncate">{tripName(trip)}</span>
            </div>
          )}
          <dl className="mt-3 flex gap-5">
            <div>
              <dt className="text-[10px] tracking-[0.12em] text-muted-foreground">
                {t.homeDepart.toUpperCase()}
              </dt>
              <dd className="mt-0.5 text-sm font-bold text-ink">
                {shortDate(trip.departureDate, fmt.locale)}
              </dd>
            </div>
            {trip.returnDate && (
              <div>
                <dt className="text-[10px] tracking-[0.12em] text-muted-foreground">
                  {t.homeReturn.toUpperCase()}
                </dt>
                <dd className="mt-0.5 text-sm font-bold text-ink">
                  {shortDate(trip.returnDate, fmt.locale)}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <Perforation />

        <div className="flex w-[112px] flex-shrink-0 flex-col items-center justify-center gap-1.5 px-2 py-4 text-center">
          <ReadinessRing percent={percent} size={80} />
          <div className="text-xs font-semibold text-muted-foreground">
            {percent === null ? t.readinessNotChecked : t.readinessCaption}
          </div>
          {nights !== null && nights > 0 && (
            <div className="text-[11px] text-muted-foreground">
              {fill(t.homeNights, { n: nights })}
            </div>
          )}
        </div>
      </div>

      <Link
        to={`/traveler/travel/${trip._id}/readiness`}
        className={`flex min-h-[52px] items-center justify-between px-5 text-[15px] font-bold ${
          percent === 100
            ? "bg-health-600 text-white"
            : "bg-travel-500 text-ink"
        }`}
      >
        {strip}
        <ChevronRight className="h-5 w-5" aria-hidden="true" />
      </Link>

      {moreCount > 0 && (
        <Link
          to="/traveler/travels"
          className="flex min-h-[44px] items-center justify-center text-sm font-semibold text-primary-700"
        >
          {fill(t.homeMoreTrips, { n: moreCount })}
        </Link>
      )}
    </section>
  );
};
